// The media header field, mirroring the library's MediaScreen (Components/DragAndDrop): the URL / upload radio, dropzone,
// loading state, "Media URL" box and live "Enter a valid URL" hint, with its classNames and texts. The library's bundle
// injects the nic-dnd-* stylesheet when it loads, so none of ours is needed. Differences:
// - the host uploads (`onUpload`) instead of callbackMethod('upload'), and reports a failed upload itself;
// - a hidden <input type="file"> with click and drop handlers stands in for react-dropzone;
// - a file of the wrong type gets an "Invalid file!" toast; the library uploads it anyway (all kinds but GIF), and Save then depends on the URL that comes back.
// Without `onUpload` the field is the URL box alone.
import { useEffect, useRef, useState } from 'react';
import {
  Box, CircularProgress, Radio, TextField, Toast,
} from '@gupshup-frontend/gupshup-ts-components';
import { isValidUrl, MEDIA_TOKEN_RE } from '../shared/validators';

// Per header kind, from the library's getAcceptedExtension (Helper/Utils.tsx:155), DRAG_DROP_FIELD_DISCRIPTION
// and FILE_MAXSIZE (Helper/Constants.tsx:2-8). A GIF header sends as an MP4, so it takes video like VIDEO.
const ACCEPT = {
  IMAGE: 'image/*', VIDEO: 'video/*', GIF: 'video/*', DOCUMENT: 'application/pdf',
};
const FORMATS = {
  IMAGE: 'PNG, JPG', VIDEO: 'MP4', GIF: 'MP4', DOCUMENT: 'pdf',
};
const MAX_FILE_BYTES = 4194304;
const UPLOAD_ICON = 'https://gs-upload.gupshup.io/journey-builder/assets/icon/Nodes/upload-simple.svg';
// The library's toast text for a file it can't use (MediaScreen's errorMessage after a failed upload).
const INVALID_FILE = { title: 'Invalid file!', description: 'Please upload a valid file' };

// attr-accept's rule for the two shapes above: "type/*" or one exact MIME type.
function isAccepted(file, kind) {
  const accept = ACCEPT[kind];
  return accept.endsWith('/*') ? file.type.startsWith(accept.slice(0, -1)) : file.type === accept;
}

/**
 * @typedef {object} DropzoneProps
 * @property {'IMAGE'|'VIDEO'|'DOCUMENT'|'GIF'} kind
 * @property {boolean} loading - an upload is in flight
 * @property {boolean} tooLarge - the last file picked was over the size limit
 * @property {(file: (File|undefined)) => void} onFile - the file picked or dropped, first one only
 */

/**
 * The library's DragAndDrop: a dashed box, "Click to upload or drag and drop", the formats it takes.
 * @param {DropzoneProps} props
 * @returns {JSX.Element}
 */
const Dropzone = ({
  kind, loading, tooLarge, onFile,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // react-dropzone's preventDropOnDocument: a file let go outside the box must not navigate the iframe
  // away from the form.
  useEffect(() => {
    const allowDrop = (event) => event.preventDefault();
    const blockDropOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) event.preventDefault();
    };
    document.addEventListener('dragover', allowDrop, false);
    document.addEventListener('drop', blockDropOutside, false);
    return () => {
      document.removeEventListener('dragover', allowDrop, false);
      document.removeEventListener('drop', blockDropOutside, false);
    };
  }, []);

  const openPicker = () => inputRef.current?.click();

  return (
    <Box
      sx={{
        width: '100%',
        background: '#FFFFFF',
        border: '1px dashed #C5CAD9',
        borderRadius: '4px',
        textAlign: 'center',
        padding: '25px 0',
        cursor: 'pointer',
      }}
      ref={rootRef}
      role="presentation"
      tabIndex={0}
      onClick={openPicker}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === ' ' || event.key === 'Enter')) {
          event.preventDefault();
          openPicker();
        }
      }}
      onDragEnter={(event) => event.preventDefault()}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        onFile(event.dataTransfer?.files?.[0]);
      }}
      data-testid="bloomreach-standard-send-media-dropzone"
    >
      <div className="drop-files-bg">
        <input
          ref={inputRef}
          type="file"
          className="drop-files-input"
          accept={ACCEPT[kind]}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
          // The input's own click would bubble to the box and open the picker again.
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            const input = event.target;
            onFile(input.files?.[0]);
            // Cleared so picking the same file again still fires a change.
            input.value = '';
          }}
          data-testid="bloomreach-standard-send-media-file"
        />
        {loading
          ? <CircularProgress data-testid="circular-progress-testid" thickness={2.5} />
          // The library's own alt text (DragAndDrop.tsx:87), kept as it is.
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          : <img src={UPLOAD_ICON} height="25px" width="25px" alt="add image" />}
        <div className="nic-dnd-text-field">Click to upload or drag and drop</div>
        <div className="nic-dnd-text-field">{FORMATS[kind]} up to 4MB</div>
        {tooLarge && <div className="nic-dnd-error-file">{kind.toLowerCase()} is too large.</div>}
      </div>
    </Box>
  );
};

/**
 * @typedef {object} MediaFieldProps
 * @property {'IMAGE'|'VIDEO'|'DOCUMENT'|'GIF'} kind - the template's header kind
 * @property {string} value - the media URL as typed
 * @property {(value: string) => void} onChange - the typed value, or the URL an upload returned
 * @property {(file: File) => Promise<(string|null)>} [onUpload] - the host uploads the file and hands back
 *   its URL, or null when it can't; the host reports that failure, this field shows nothing for it. Without
 *   it there is no upload option.
 * @property {boolean} [allowTokens] - a whole `{token}` is a fine value, so the live hint leaves it alone. False
 *   (the library's rule): the value has to be a URL.
 */

/**
 * @param {MediaFieldProps} props
 * @returns {JSX.Element}
 */
const MediaField = ({
  kind, value, onChange, onUpload, allowTokens = false,
}) => {
  const [uploadType, setUploadType] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [tooLarge, setTooLarge] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [changed, setChanged] = useState(false);

  const upload = async (file) => {
    if (!file || uploading) return;
    setTooLarge(false);
    if (!isAccepted(file, kind)) {
      setToastOpen(true);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setTooLarge(true);
      return;
    }
    setUploading(true);
    let url = null;
    try {
      url = await onUpload(file);
    } catch {
      // The host reports a failed upload, as it does for the shared component.
    }
    setUploading(false);
    if (typeof url === 'string' && url) onChange(url);
  };

  const label = kind.toLowerCase();
  // MediaScreen's `error` starts false, whatever value the box opens with, and is set to !isValidURL(typed) on every
  // change, so a box the marketer has emptied flags too. Here it is read off the value once the marketer has changed it.
  const liveError = changed && !isValidUrl(value) && !(allowTokens && MEDIA_TOKEN_RE.test(value?.trim()));
  const urlField = (
    <TextField
      size="small"
      label="Media URL"
      fullWidth
      error={liveError}
      disabled={uploadType === 'local'}
      helperText={liveError ? 'Enter a valid URL' : ''}
      value={value}
      InputLabelProps={{ shrink: true }}
      onChange={(e) => { setChanged(true); onChange(e.target.value); }}
      data-testid="bloomreach-standard-send-media"
    />
  );

  if (!onUpload) return urlField;

  return (
    <>
      <Radio
        color="primary"
        size="small"
        value={uploadType}
        onChange={(e) => setUploadType(e.target.value)}
        data={[
          { 'data-testid': 'radio-global-testid', label: `Use ${label} URL`, value: 'url' },
          { 'data-testid': 'radio-local-testid', label: `Choose ${label}`, value: 'local' },
        ]}
      />
      <div>
        {uploadType === 'local' && (
          <Dropzone kind={kind} loading={uploading} tooLarge={tooLarge} onFile={upload} />
        )}
        <br />
        {urlField}
        <Toast
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          data-testid="toast-testid"
          maxWidth="30vw"
          onClose={() => setToastOpen(false)}
          title={INVALID_FILE.title}
          description={INVALID_FILE.description}
          type="error"
          open={toastOpen}
          autoHideDuration={3000}
        />
      </div>
    </>
  );
};

export default MediaField;
